import {
  ColumnTypes,
  editableComponents,
  IEditableCellProps,
} from "@/containers/ProjectTemplate";
import { nameof } from "@/helpers";
import {
  IFormFieldOptions,
  IMilestoneTemplate,
  INotificationGroup,
} from "@/interfaces";
import { useGetFormLookupsQuery } from "@/services";
import { FieldType } from "@/types";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faPlusCircle, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Table } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { v4 } from "uuid";

interface IProps {
  milestoneTemplate: IMilestoneTemplate;
  updateMilestone(data: Partial<IMilestoneTemplate>): void;
}

export const TemplateCoreTeamNotificationGroups = ({
  milestoneTemplate,
  updateMilestone,
}: IProps) => {
  const intl = useIntl();

  const { data: { intake } = {} } = useGetFormLookupsQuery();

  const [coreTeamGroups, setCoreTeamGroups] = useState<INotificationGroup[]>(
    []
  );

  useEffect(() => {
    setCoreTeamGroups(milestoneTemplate.notification?.coreTeamGroups);
  }, [milestoneTemplate?.notification]);

  const updateModalState = (updatedGroups: INotificationGroup[]): void => {
    setCoreTeamGroups(updatedGroups);
    updateMilestone({
      notification: {
        ...milestoneTemplate.notification,
        coreTeamGroups: updatedGroups,
      },
    });
  };

  const handleAdd = (): void => {
    const newGroup = [
      {
        id: v4(),
      } as INotificationGroup,
    ];

    const updatedGroups = newGroup.concat(coreTeamGroups);

    updateModalState(updatedGroups);
  };

  const handleDelete = (id: string): void => {
    const updatedGroups = coreTeamGroups.filter((group) => group.id !== id);
    setCoreTeamGroups(updatedGroups);
    updateModalState(updatedGroups);
  };

  const handleUpdate = (row: INotificationGroup): void => {
    const updatedGroups = [...coreTeamGroups];
    const index = updatedGroups.findIndex((group) => row.id === group.id);
    const item = updatedGroups[index];

    updatedGroups.splice(index, 1, {
      ...item,
      ...row,
    });

    updateModalState(updatedGroups);
  };

  const defaultColumns: (ColumnTypes[number] & {
    type: FieldType | undefined;
    dataIndex: string;
    required?: boolean;
    editable?: boolean;
    options?: IFormFieldOptions;
    placeholder?: string;
    showSearch?: boolean;
  })[] = [
    {
      title: "Core Team Group",
      dataIndex: nameof<INotificationGroup>("groupId"),
      key: "group",
      width: "350px",
      type: undefined,
      required: true,
      editable: false,
      render: () => <>{"Core Team"}</>,
    },
    {
      title: intl.formatMessage({
        id: "milestoneTemplates.columns.productCategory",
      }),
      dataIndex: nameof<INotificationGroup>("defaultForProductCategories"),
      key: "productCategory",
      width: "750px",
      fixed: true,
      type: "MultiSelectDropDown",
      required: false,
      editable: true,
      options: intake?.productCategory ?? {},
      placeholder: intl.formatMessage({ id: "general.all" }),
    },
    {
      title: intl.formatMessage({ id: "milestoneTemplates.columns.required" }),
      dataIndex: nameof<INotificationGroup>("required"),
      key: "required",
      width: "85px",
      type: "Checkbox",
      editable: true,
    },
    {
      title: (
        <>
          {coreTeamGroups?.length < 1 && (
            <Button
              data-test-id="milestone-template-notification-groups-add"
              type="link"
              onClick={handleAdd}
              icon={
                <FontAwesomeIcon icon={faPlusCircle as IconProp} size="xl" />
              }
            />
          )}
        </>
      ),
      dataIndex: nameof<INotificationGroup>("id"),
      key: "delete",
      editable: false,
      type: undefined,
      render: (id: string) => (
        <Button
          data-test-id="milestone-template-notification-groups-delete"
          type="link"
          onClick={(): void => handleDelete(id)}
          icon={<FontAwesomeIcon icon={faTrash as IconProp} color="red" />}
        />
      ),
    },
  ];

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: INotificationGroup) =>
        ({
          record,
          editable: col.editable,
          dataIndex: col.dataIndex,
          title: col.title,
          type: col.type,
          required: col.required,
          options: col.options,
          showSearch: col.showSearch,
          handleSave: handleUpdate,
          placeholder: col.placeholder,
        } as IEditableCellProps<INotificationGroup>),
    };
  });

  return (
    <Table
      components={editableComponents}
      style={{ margin: "-12px 0px" }}
      tableLayout="fixed"
      className="w-full"
      size="small"
      columns={columns as ColumnTypes}
      dataSource={coreTeamGroups}
      rowKey="id"
      pagination={false}
    />
  );
};
